import React, { memo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Divider,
  IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Check, Copy } from '@phosphor-icons/react';
import WaveSurferComponent from './WaveSurfer';
import { CallTranscriptionSkeleton } from '../Skeletons/CallTranscriptionSkeleton';
import theme from '../../../theme';
import useCopyToClipboard from '../../../hooks/useCopyToClipboard';

interface Message {
  role: 'user' | 'assistant';
  message: string;
}

export interface CallDetailTabsProps {
  messages: Message[];
  id: string;
  agentName: string;
  callType: string;
  to: string;
  from: string;
  config: {
    tts: string;
    llm: string;
    stt: string;
  };
  isLoading: boolean;
  audio: string;
  cost: string;
  billedFor: string;
  sid: string;
  selectedAssistantId?: string;
}

const MessageContainer = styled(Paper)(({ theme }) => ({
  padding: '16px',
  marginBottom: '12px',
  borderRadius: '12px',
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  backgroundColor: '#FFF',
}));

const RoleLabel = styled(Typography)<{ role: string }>(({ theme, role }) => ({
  marginBottom: '8px',
  fontSize: '14px',
  fontWeight: 500,
  color: role === 'Client' ? '#FF4D6A' : theme.palette.primary.main,
}));

const MessageContent = styled(Typography)({
  color: '#2B2D3F',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: 'normal',
  letterSpacing: '-0.16px',
  textWrap: 'wrap',
  overflowWrap: 'break-word',
  wordBreak: 'break-word',
  whiteSpace: 'pre-wrap',
});

const CallTranscription: React.FC<CallDetailTabsProps> = memo(
  ({
    from,
    agentName,
    callType,
    isLoading,
    audio,
    messages,
    id,
    to,
    cost,
    billedFor,
  }) => {
    const { copied, copy } = useCopyToClipboard();
    const handleCopyClick = () => {
      if (id) {
        copy(id);
      }
    };

    if (isLoading) {
      return <CallTranscriptionSkeleton />;
    }
    // Memoize filtered messages
    const validMessages = messages.filter(
      message => message.message.trim() !== ''
    );

    // Memoize DetailRow component
    const DetailRow = memo<DetailRowProps>(({ label, value, copyable }) => {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{
              color: '#6B7280',
              fontSize: '14px',
              fontWeight: 500,
            }}
          >
            {label}:
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography
              sx={{
                color: '#111827',
                fontSize: '14px',
                fontWeight: 400,
              }}
            >
              {value}
            </Typography>
            {copyable && (
              <IconButton
                size="small"
                sx={{ p: 0.5 }}
                onClick={handleCopyClick}
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
              </IconButton>
            )}
          </Box>
        </Box>
      );
    });

    return (
      <Box sx={{ display: 'flex', height: '100%' }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <WaveSurferComponent audioUrl={audio} isLoading={isLoading} />
          <Divider />
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              p: 2,
              height: '100%',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
            }}
          >
            {validMessages.length > 0 ? (
              validMessages.map((message, index) => (
                <MessageContainer key={index}>
                  <RoleLabel
                    role={message.role}
                    variant="body2"
                    sx={{
                      color:
                        message.role === 'user'
                          ? '#FF4D6A'
                          : theme.palette.primary.main,
                      fontSize: '14px',
                      fontWeight: 500,
                      textTransform: 'capitalize',
                    }}
                  >
                    {message.role === 'user' ? 'Client' : 'Assistant'}
                  </RoleLabel>
                  <MessageContent variant="body1">
                    {message.message}
                  </MessageContent>
                </MessageContainer>
              ))
            ) : (
              <Typography
                variant="body1"
                sx={{
                  textAlign: 'center',
                  color: '#6B7280',
                  mt: 6,
                }}
              >
                No messages available
              </Typography>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            width: '300px',
            borderLeft: '1px solid #E5E7EB',
            backgroundColor: '#FFF',
            p: 1,
          }}
        >
          <Stack spacing={1.5} divider={<Divider />}>
            <DetailRow label="Agent Name" value={agentName} />
            <DetailRow label="Id" value={id} copyable />
            <DetailRow label="From" value={from} />
            <DetailRow label="To" value={to} />
            <DetailRow label="Call Type" value={callType} />
            <DetailRow label="Cost" value={`$${cost}`} />
            <DetailRow label="Billed For" value={`${billedFor} Sec`} />
          </Stack>
        </Box>
      </Box>
    );
  }
);

interface DetailRowProps {
  label: string;
  value: string;
  copyable?: boolean;
}

export default CallTranscription;
